import {jwtDecrypt, tokenAlive} from '@/plugins/jwtHelper'
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { defineStore } from 'pinia'
import * as Sentry from "@sentry/vue"; // <--- 1. Import Sentry
import { AppGroupBridgePlugin } from '@/plugins/AppGroupBridgePlugin'
import { scheduleProactiveTokenRefresh, cancelProactiveTokenRefresh } from '@/plugins/authService'

// Module-level promise so callers (e.g. router guard, jwtInterceptor) can await
// an in-flight refresh and receive the new token directly — avoiding Capacitor
// Preferences bridge latency on iOS/Android.
let _refreshPromise = null;
let _refreshResolve = null;
let _refreshReject = null;
// Last successfully refreshed token — used by waitForTokenRefresh() as a fallback
// when _refreshPromise is null. Covers the race where a 401 waiter arrives after
// resolveTokenRefresh() already nulled _refreshPromise (DEVAMS-2053).
let _lastRefreshedToken = null;

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: "",
        refreshToken: "",
        tokenExp: "",
        userId: "",
        userName: "",
        isLoggedIn: false,
        isDemoUser: false,
        isTokenRefreshing: false,
    }),
    persist: {
        enabled: true,
        paths: ['token', 'refreshToken', 'tokenExp', 'userId', 'userName', 'isLoggedIn', 'isDemoUser'],
    },
    getters: {
        getIsLoggedInState: (state) => {
            return state.isLoggedIn;
        },
        isTokenActive: async(state) => {
            if (state.tokenExp === "") {
                return false;
            }
            return tokenAlive(state.tokenExp);
        },
        getJWTToken: (state) => {
            return state.token
        },
        getUsersign: (state) => {
            return state.userName
        },
        getAuthData: (state ) => {
            return state;
        },
        getTokenRefreshingInProgress: (state) => {
            return state.isTokenRefreshing;
        }
    },
    actions: {
        async login(payload) {
            if (payload?.access_token !== undefined) {
                await this.saveTokenData(payload);
                this.isLoggedIn = true;
                return true
            } else {
                this.isLoggedIn = false;
                return payload
            }
        },
        async saveTokenData(data) {
            // IN CASE A DEMO USER VIEWS THE APP
            if(data.isDemo !== undefined && data.isDemo === true) {
                await Preferences.set({
                    key: 'access_token',
                    value: data.access_token,
                })
                await Preferences.set({
                    key: 'refresh_token',
                    value: data.refresh_token,
                })
                this.token = data.access_token;
                this.refreshToken = data.refresh_token;
                this.tokenExp = 1924991999;
                this.userId = "demo@nayak.aero";
                this.userName = "demo";
                this.isDemoUser = true;

                // <--- 2. Set Sentry User for Demo
                Sentry.setUser({
                    id: 'demo-user',
                    username: 'demo',
                    email: 'demo@nayak.aero',
                    segment: 'demo'
                });

                return;
            }
            // END DEMO SECTION

            // this is real deal for actual users
            const token = data.access_token ?? data.token;
            if (token == null) {
                return;
            }
            const jwtDecodedValue = jwtDecrypt(token);
            // remove @domain.tlc to get clean username for all countries
            const cleanUserName = jwtDecodedValue.upn.split("@")[0].toLowerCase()
            this.token = token
            this.refreshToken = data.refresh_token
            this.tokenExp = jwtDecodedValue.exp
            this.userId = jwtDecodedValue.upn
            this.userName = cleanUserName
            this.isDemoUser = false

            // Await Preferences writes so callers (jwtInterceptor, router guard) can
            // safely read back the new token via Preferences.get immediately after
            // saveTokenData resolves — important on native Capacitor where the
            // Preferences bridge is truly async with real latency.
            await Preferences.set({
                key: 'access_token',
                value: token,
            })
            await Preferences.set({
                key: 'refresh_token',
                value: data.refresh_token,
            })
            await Preferences.set({
                key: 'tokenExp',
                value: jwtDecodedValue.exp,
            })

            // <--- 3. Set Sentry User for Real Users
            // We use the decoded JWT data you already calculated
            Sentry.setUser({
                id: jwtDecodedValue.upn, // using email/UPN as ID
                email: jwtDecodedValue.upn,
                username: cleanUserName,
                // Optional: Add extra data useful for debugging
                segment: 'production',
            });

            // Write token to App Group container so the iOS ShareExtension can
            // authenticate API calls directly without opening the main app.
            if (Capacitor.getPlatform() === 'ios') {
                await AppGroupBridgePlugin.saveAccessToken({
                    token: token,
                    apiBaseUrl: import.meta.env.VITE_API_URL ?? '',
                })
            }

            // Schedule a proactive background refresh 5 min before expiry so the
            // token never expires unnoticed while the user is on a long-running page.
            scheduleProactiveTokenRefresh(jwtDecodedValue.exp);
        },
        setLoginStatus(value){
            this.isLoggedIn = value;
        },
        logout(eventBus) {

            console.debug("logging out")
            cancelProactiveTokenRefresh();
            console.info("send logout event to unsubscribe from all active FCM subscriptions")

            // <--- 4. Clear Sentry User on Logout
            // This ensures subsequent errors aren't attributed to the previous user
            Sentry.setUser(null);

            eventBus.emit('logout', "logout");
            Preferences.remove({key: 'access_token'})
            Preferences.remove({key: 'refresh_token'})
            Preferences.remove({key: 'tokenExp'})
            sessionStorage.clear()
            this.isLoggedIn = false
            this.isDemoUser = false
            this.token = ""
            this.refreshToken= ""
            this.tokenExp= ""
            this.userName= ""

            // Remove token from App Group container so the ShareExtension no longer
            // has credentials to use after logout.
            if (Capacitor.getPlatform() === 'ios') {
                AppGroupBridgePlugin.clearAccessToken()
            }

            // Reject any in-flight refresh waiters so they don't hang forever.
            this.rejectTokenRefresh(new Error('Logged out'));
            console.debug("logout")
        },
        setTokenRefreshing(value){
            this.isTokenRefreshing = value;
            if (value) {
                _lastRefreshedToken = null;
                _refreshPromise = new Promise((resolve, reject) => {
                    _refreshResolve = resolve;
                    _refreshReject = reject;
                });
                // Attach a no-op catch so that if rejectTokenRefresh() is called
                // without any waitForTokenRefresh() callers, Node.js does not flag
                // _refreshPromise as an unhandled rejection.
                _refreshPromise.catch(() => {});
            } else {
                // Resolve with null — used by the "already in progress, just wait" path
                // where the caller only needs to know the refresh is done, not the token.
                _lastRefreshedToken = null;
                _refreshResolve?.(null);
                _refreshPromise = null;
                _refreshResolve = null;
                _refreshReject = null;
            }
        },
        /**
         * Called by whoever performed the refresh on success.
         * Resolves all waiters with the new access token directly (no Preferences read needed).
         */
        resolveTokenRefresh(token) {
            this.isTokenRefreshing = false;
            _lastRefreshedToken = token;
            _refreshResolve?.(token);
            _refreshPromise = null;
            _refreshResolve = null;
            _refreshReject = null;
        },
        /**
         * Called by whoever performed the refresh on failure.
         * Rejects all waiters so they can propagate the error / redirect to login.
         */
        rejectTokenRefresh(error) {
            this.isTokenRefreshing = false;
            _lastRefreshedToken = null;
            _refreshReject?.(error);
            _refreshPromise = null;
            _refreshResolve = null;
            _refreshReject = null;
        },
        waitForTokenRefresh() {
            return _refreshPromise ?? Promise.resolve(_lastRefreshedToken);
        }
    },
})
